import * as THREE from "three";
import BaseThreeScene from "../../scenes/BaseThreeScene";
import { createGraphEdge, createGraphNode } from "../../scenes/graphPrimitives";
import { buildUndirectedAdjacency, createCircularGraphLayout } from "../../scenes/shared/graphLayout";

const DEFAULT_GRAPH_INPUT = {
    startId: 1,
    nodes: [1, 2, 3, 4],
    edges: [
        [1, 2],
        [2, 3],
        [3, 4],
        [4, 1],
    ],
};

const ORIGINAL_NODE_COLOR = new THREE.Color(0x60a5fa);
const ORIGINAL_NODE_VISITED_COLOR = new THREE.Color(0x7dd3fc);
const ORIGINAL_NODE_DONE_COLOR = new THREE.Color(0x34d399);
const ORIGINAL_NODE_ACTIVE_COLOR = new THREE.Color(0xe0f2fe);

const CLONE_NODE_COLOR = new THREE.Color(0xa78bfa);
const CLONE_NODE_ACTIVE_COLOR = new THREE.Color(0xf5d0fe);
const CLONE_NODE_DONE_COLOR = new THREE.Color(0xc4b5fd);

const ORIGINAL_EDGE_COLOR = new THREE.Color(0x334155);
const ORIGINAL_EDGE_ACTIVE_COLOR = new THREE.Color(0x7dd3fc);

const CLONE_EDGE_COLOR = new THREE.Color(0x8b5cf6);
const CLONE_EDGE_ACTIVE_COLOR = new THREE.Color(0xf0abfc);

const GRAPH_SCALE = 1.05;
const ORIGINAL_OFFSET = new THREE.Vector3(-4.1, 0, 0);
const CLONE_OFFSET = new THREE.Vector3(4.1, 0, 0);

function edgeKey(a, b) {
    return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function normalizeGraphInput(input) {
    const source = input || DEFAULT_GRAPH_INPUT;

    return {
        startId: source.startId,
        nodes: source.nodes.slice().sort((a, b) => a - b),
        edges: source.edges.map((edge) => edge.slice()),
    };
}

function buildCloneSteps(graphInput) {
    const { startId, nodes, edges } = graphInput;
    const adjacency = buildUndirectedAdjacency(nodes, edges);
    const queue = [startId];
    const enqueued = new Set([startId]);
    const createdNodes = new Set();
    const createdEdges = new Set();
    const steps = [];

    steps.push({ type: "focus-node", nodeId: startId });
    steps.push({ type: "clone-node", sourceId: startId, cloneId: startId });

    createdNodes.add(startId);

    while (queue.length > 0) {
        const nodeId = queue.shift();

        steps.push({ type: "focus-node", nodeId });

        for (const neighborId of adjacency.get(nodeId) || []) {
            steps.push({
                type: "inspect-edge",
                fromId: nodeId,
                toId: neighborId,
            });

            if (!createdNodes.has(neighborId)) {
                createdNodes.add(neighborId);
                steps.push({
                    type: "clone-node",
                    sourceId: neighborId,
                    cloneId: neighborId,
                });
            }

            const nextEdgeKey = edgeKey(nodeId, neighborId);
            if (!createdEdges.has(nextEdgeKey)) {
                createdEdges.add(nextEdgeKey);
                steps.push({
                    type: "clone-edge",
                    fromId: nodeId,
                    toId: neighborId,
                });
            }

            if (!enqueued.has(neighborId)) {
                enqueued.add(neighborId);
                queue.push(neighborId);
                steps.push({
                    type: "enqueue-node",
                    nodeId: neighborId,
                    fromId: nodeId,
                });
            }
        }

        steps.push({ type: "finish-node", nodeId });
    }

    steps.push({ type: "complete" });

    return steps;
}

function setMaterialOpacity(material, opacity) {
    material.transparent = opacity < 0.999;
    material.opacity = opacity;
}

function createStepLogEntry(step, stepIndex) {
    const stepNumber = stepIndex + 1;

    switch (step.type) {
        case "focus-node":
            return {
                id: `clone-graph-step-${stepNumber}-focus-node-${step.nodeId}`,
                stepIndex: stepNumber,
                kind: "Focus",
                title: `Focus node ${step.nodeId}`,
                message: `Moves BFS attention to node ${step.nodeId} so its adjacency list can be processed.`,
            };

        case "inspect-edge":
            return {
                id: `clone-graph-step-${stepNumber}-inspect-edge-${step.fromId}-${step.toId}`,
                stepIndex: stepNumber,
                kind: "Inspect",
                title: `Inspect edge ${step.fromId} -> ${step.toId}`,
                message: `Reads neighbor ${step.toId} while iterating node ${step.fromId}'s adjacency list.`,
            };

        case "clone-node":
            return {
                id: `clone-graph-step-${stepNumber}-clone-node-${step.cloneId}`,
                stepIndex: stepNumber,
                kind: "Clone",
                title: `Create clone node ${step.cloneId}`,
                message: `Materializes node ${step.cloneId} in the cloned graph and links it to the original source node for the transfer animation.`,
            };

        case "clone-edge":
            return {
                id: `clone-graph-step-${stepNumber}-clone-edge-${step.fromId}-${step.toId}`,
                stepIndex: stepNumber,
                kind: "Clone",
                title: `Create clone edge ${step.fromId} <-> ${step.toId}`,
                message: `Builds the corresponding undirected edge between cloned nodes ${step.fromId} and ${step.toId}.`,
            };

        case "enqueue-node":
            return {
                id: `clone-graph-step-${stepNumber}-enqueue-node-${step.nodeId}`,
                stepIndex: stepNumber,
                kind: "Queue",
                title: `Enqueue node ${step.nodeId}`,
                message: `Adds node ${step.nodeId} to the BFS queue after discovering it from node ${step.fromId}.`,
            };

        case "finish-node":
            return {
                id: `clone-graph-step-${stepNumber}-finish-node-${step.nodeId}`,
                stepIndex: stepNumber,
                kind: "Finish",
                title: `Finish node ${step.nodeId}`,
                message: `Marks node ${step.nodeId} as fully processed after all neighbors have been handled.`,
            };

        case "complete":
            return {
                id: `clone-graph-step-${stepNumber}-complete`,
                stepIndex: stepNumber,
                kind: "Done",
                title: "Clone complete",
                message: "All reachable nodes and edges have been copied into the cloned graph.",
            };

        default:
            return {
                id: `clone-graph-step-${stepNumber}-unknown`,
                stepIndex: stepNumber,
                kind: "Step",
                title: "Advance step",
                message: "Advances the scene to the next animation step.",
            };
    }
}

export default class CloneGraphScene extends BaseThreeScene {
    setup(props) {
        this.graphInput = normalizeGraphInput(props.input);
        this.nodeLayout = createCircularGraphLayout(this.graphInput.nodes);
        this.edgeLayout = this.graphInput.edges.map((edge) => edge.slice());

        const maxGraphRadius = this.nodeLayout.reduce(
            (maxRadius, node) => Math.max(maxRadius, node.position.length()),
            1.6
        );

        this.scene.background = new THREE.Color(0x050816);
        this.scene.fog = new THREE.Fog(0x050816, 14, 28);

        this.camera.position.set(0, 0.1, 8.8 + maxGraphRadius * 1.7);

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.05);
        this.scene.add(ambientLight);

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.7);
        keyLight.position.set(5, 7, 10);
        this.scene.add(keyLight);

        const leftLight = new THREE.PointLight(0x60a5fa, 14, 16, 2);
        leftLight.position.set(-6, 2.5, 4);
        this.scene.add(leftLight);

        const rightLight = new THREE.PointLight(0xa78bfa, 14, 16, 2);
        rightLight.position.set(6, 2.5, 4);
        this.scene.add(rightLight);

        this.graphsRoot = new THREE.Group();
        this.scene.add(this.graphsRoot);

        this.originalNodes = new Map();
        this.cloneNodes = new Map();
        this.originalEdges = new Map();
        this.cloneEdges = new Map();

        this.originalPositions = new Map();
        this.clonePositions = new Map();
        this.presentationTime = 0;

        this.buildGraphs();
        this.buildIndicators();
        this.resetAnimationState();
        this.advanceStep();
    }

    buildGraphs() {
        for (const nodeData of this.nodeLayout) {
            const originalPosition = nodeData.position
                .clone()
                .multiplyScalar(GRAPH_SCALE)
                .add(ORIGINAL_OFFSET);
            const clonePosition = nodeData.position
                .clone()
                .multiplyScalar(GRAPH_SCALE)
                .add(CLONE_OFFSET);

            this.originalPositions.set(nodeData.id, originalPosition);
            this.clonePositions.set(nodeData.id, clonePosition);

            const originalNode = createGraphNode({
                id: nodeData.id,
                position: originalPosition,
                color: ORIGINAL_NODE_COLOR.getHex(),
            });

            const cloneNode = createGraphNode({
                id: nodeData.id,
                position: clonePosition,
                color: CLONE_NODE_COLOR.getHex(),
            });

            cloneNode.scale.setScalar(0.001);
            setMaterialOpacity(cloneNode.material, 0);
            cloneNode.material.transparent = true;

            this.originalNodes.set(nodeData.id, originalNode);
            this.cloneNodes.set(nodeData.id, cloneNode);

            this.graphsRoot.add(originalNode);
            this.graphsRoot.add(cloneNode);
        }

        for (const [fromId, toId] of this.edgeLayout) {
            const nextEdgeKey = edgeKey(fromId, toId);

            const originalEdge = createGraphEdge({
                fromId,
                toId,
                start: this.originalPositions.get(fromId),
                end: this.originalPositions.get(toId),
                color: ORIGINAL_EDGE_COLOR.getHex(),
                opacity: 0.42,
            });

            const cloneEdge = createGraphEdge({
                fromId,
                toId,
                start: this.clonePositions.get(fromId),
                end: this.clonePositions.get(toId),
                color: CLONE_EDGE_COLOR.getHex(),
                opacity: 0,
            });

            cloneEdge.material.transparent = true;

            this.originalEdges.set(nextEdgeKey, originalEdge);
            this.cloneEdges.set(nextEdgeKey, cloneEdge);

            this.graphsRoot.add(originalEdge);
            this.graphsRoot.add(cloneEdge);
        }
    }

    buildIndicators() {
        const ringGeometry = new THREE.TorusGeometry(0.44, 0.04, 16, 64);

        this.originalActiveRing = new THREE.Mesh(
            ringGeometry,
            new THREE.MeshBasicMaterial({
                color: 0x93c5fd,
                transparent: true,
                opacity: 0.92,
            })
        );
        this.originalActiveRing.rotation.x = Math.PI * 0.5;
        this.graphsRoot.add(this.originalActiveRing);

        this.cloneActiveRing = new THREE.Mesh(
            ringGeometry,
            new THREE.MeshBasicMaterial({
                color: 0xf0abfc,
                transparent: true,
                opacity: 0.92,
            })
        );
        this.cloneActiveRing.rotation.x = Math.PI * 0.5;
        this.graphsRoot.add(this.cloneActiveRing);

        const pulseGeometry = new THREE.SphereGeometry(0.12, 18, 18);
        this.transferPulse = new THREE.Mesh(
            pulseGeometry,
            new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0,
            })
        );
        this.scene.add(this.transferPulse);

        this.cloneBuildBeamGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(),
            new THREE.Vector3(),
        ]);

        this.cloneBuildBeam = new THREE.Line(
            this.cloneBuildBeamGeometry,
            new THREE.LineBasicMaterial({
                color: CLONE_EDGE_ACTIVE_COLOR.getHex(),
                transparent: true,
                opacity: 0,
            })
        );
        this.scene.add(this.cloneBuildBeam);
    }

    resetAnimationState() {
        const { startId, nodes, edges } = this.graphInput;

        this.adjacency = buildUndirectedAdjacency(nodes, edges);
        this.steps = buildCloneSteps(this.graphInput);
        this.stepIndex = -1;
        this.currentStep = null;
        this.currentStepElapsed = 0;

        this.activeOriginalNodeId = null;
        this.activeCloneNodeId = null;
        this.activeOriginalEdgeKey = null;
        this.activeCloneEdgeKey = null;
        this.pendingCloneEdgeKey = null;

        this.clonedNodeIds = new Set();
        this.clonedEdgeKeys = new Set();
        this.enqueuedNodeIds = new Set([1]);
        this.processedNodeIds = new Set();
        this.visitedNodeIds = new Set();

        this.traversalMode = "BFS";
        this.bfsQueue = [1];
        this.visitOrder = [];
        this.cloneNodeMap = new Map();

        this.transferPulseState = {
            active: false,
            from: new THREE.Vector3(),
            to: new THREE.Vector3(),
            elapsed: 0,
            duration: 0.9,
        };

        this.cloneEdgeBuildState = {
            active: false,
            from: new THREE.Vector3(),
            to: new THREE.Vector3(),
            elapsed: 0,
            duration: 0.7,
        };

        this.presentationTime = 0;
        this.resetStepLog([]);

        this.replaceUiState({
            traversalMode: this.traversalMode,
            activeNodeId: null,
            frontier: this.bfsQueue.slice(),
            visited: [],
            processed: [],
            mapPairs: [],
        });

        this.syncPlaybackState();
    }

    syncUiState() {
        const mapPairs = Array.from(this.cloneNodeMap.entries())
            .map(([sourceId, cloneId]) => ({
                sourceId,
                cloneId,
            }))
            .sort((a, b) => a.sourceId - b.sourceId);

        this.replaceUiState({
            traversalMode: this.traversalMode,
            activeNodeId: this.activeOriginalNodeId,
            frontier: this.bfsQueue.slice(),
            visited: this.visitOrder.slice(),
            processed: Array.from(this.processedNodeIds).sort((a, b) => a - b),
            mapPairs,
        });
    }

    getStepDuration(step) {
        switch (step.type) {
            case "focus-node":
                return 0.9;
            case "inspect-edge":
                return 0.8;
            case "clone-node":
                return 1.0;
            case "clone-edge":
                return 0.9;
            case "enqueue-node":
                return 0.55;
            case "finish-node":
                return 0.55;
            case "complete":
                return 2.2;
            default:
                return 0.8;
        }
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        });
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1;
        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState();
            this.syncUiState();
            return false;
        }

        this.stepIndex = nextStepIndex;
        this.currentStepElapsed = 0;
        this.currentStep = this.steps[this.stepIndex] || null;
        this.applyStep(this.currentStep);
        this.appendStepLog(createStepLogEntry(this.currentStep, this.stepIndex));
        this.syncPlaybackState();
        this.syncUiState();

        return true;
    }

    applyStep(step) {
        if (!step) {
            return;
        }

        this.activeOriginalEdgeKey = null;
        this.activeCloneEdgeKey = null;

        switch (step.type) {
            case "focus-node": {
                this.activeOriginalNodeId = step.nodeId;
                this.activeCloneNodeId = this.clonedNodeIds.has(step.nodeId)
                    ? step.nodeId
                    : null;

                if (this.bfsQueue.length > 0 && this.bfsQueue[0] === step.nodeId) {
                    this.bfsQueue.shift();
                } else {
                    this.bfsQueue = this.bfsQueue.filter((nodeId) => nodeId !== step.nodeId);
                }

                this.enqueuedNodeIds.delete(step.nodeId);

                if (!this.visitedNodeIds.has(step.nodeId)) {
                    this.visitedNodeIds.add(step.nodeId);
                    this.visitOrder.push(step.nodeId);
                }

                break;
            }

            case "inspect-edge": {
                this.activeOriginalNodeId = step.fromId;
                this.activeCloneNodeId = this.clonedNodeIds.has(step.fromId)
                    ? step.fromId
                    : null;
                this.activeOriginalEdgeKey = edgeKey(step.fromId, step.toId);
                break;
            }

            case "clone-node": {
                this.clonedNodeIds.add(step.cloneId);
                this.cloneNodeMap.set(step.sourceId, step.cloneId);
                this.activeOriginalNodeId = step.sourceId;
                this.activeCloneNodeId = step.cloneId;
                this.startTransferPulse(step.sourceId, step.cloneId);
                break;
            }

            case "clone-edge": {
                const nextEdgeKey = edgeKey(step.fromId, step.toId);
                this.pendingCloneEdgeKey = nextEdgeKey;
                this.activeOriginalNodeId = step.fromId;
                this.activeCloneNodeId = step.fromId;
                this.activeCloneEdgeKey = nextEdgeKey;
                this.startCloneEdgeBuild(step.fromId, step.toId);
                break;
            }

            case "enqueue-node": {
                this.enqueuedNodeIds.add(step.nodeId);
                this.activeOriginalNodeId = step.fromId;
                this.activeCloneNodeId = this.clonedNodeIds.has(step.fromId)
                    ? step.fromId
                    : null;

                if (!this.bfsQueue.includes(step.nodeId)) {
                    this.bfsQueue.push(step.nodeId);
                }

                break;
            }

            case "finish-node": {
                this.processedNodeIds.add(step.nodeId);
                this.activeOriginalNodeId = step.nodeId;
                this.activeCloneNodeId = this.clonedNodeIds.has(step.nodeId)
                    ? step.nodeId
                    : null;
                break;
            }

            case "complete": {
                this.activeOriginalNodeId = null;
                this.activeCloneNodeId = null;
                this.activeOriginalEdgeKey = null;
                this.activeCloneEdgeKey = null;
                this.bfsQueue = [];
                break;
            }

            default:
                break;
        }
    }

    onResetPlayback() {
        this.resetAnimationState();
        this.advanceStep();
    }

    onStepForward() {
        this.advanceStep();
    }

    startTransferPulse(sourceId, cloneId) {
        this.transferPulseState.active = true;
        this.transferPulseState.elapsed = 0;
        this.transferPulseState.from.copy(this.originalPositions.get(sourceId));
        this.transferPulseState.to.copy(this.clonePositions.get(cloneId));
    }

    startCloneEdgeBuild(fromId, toId) {
        this.cloneEdgeBuildState.active = true;
        this.cloneEdgeBuildState.elapsed = 0;
        this.cloneEdgeBuildState.from.copy(this.clonePositions.get(fromId));
        this.cloneEdgeBuildState.to.copy(this.clonePositions.get(toId));
    }

    update(delta) {
        if (!this.graphsRoot || !this.currentStep) {
            return;
        }

        const playbackDelta = this.isPlaying ? delta : 0;
        this.presentationTime += playbackDelta;

        this.currentStepElapsed += playbackDelta;
        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep();
            if (!advanced) {
                this.pause();
            }
        }

        this.updateTransferPulse(playbackDelta);
        this.updateCloneBuildBeam(playbackDelta);
        this.updateGraphMotion(this.presentationTime);
        this.updateOriginalNodes(this.presentationTime);
        this.updateCloneNodes(this.presentationTime);
        this.updateOriginalEdges();
        this.updateCloneEdges();
        this.updateActiveRings(this.presentationTime);
    }

    updateTransferPulse(delta) {
        const state = this.transferPulseState;

        if (!state.active) {
            this.transferPulse.material.opacity = THREE.MathUtils.lerp(
                this.transferPulse.material.opacity,
                0,
                0.18
            );
            return;
        }

        state.elapsed += delta;
        const t = Math.min(state.elapsed / state.duration, 1);
        const eased = THREE.MathUtils.smoothstep(t, 0, 1);

        this.transferPulse.position.lerpVectors(state.from, state.to, eased);
        this.transferPulse.scale.setScalar(1 + Math.sin(eased * Math.PI) * 0.6);
        this.transferPulse.material.opacity = 0.95 * Math.sin(eased * Math.PI);

        if (t >= 1) {
            state.active = false;
        }
    }

    updateCloneBuildBeam(delta) {
        const state = this.cloneEdgeBuildState;
        const positions = this.cloneBuildBeamGeometry.attributes.position.array;

        if (!state.active) {
            this.cloneBuildBeam.material.opacity = THREE.MathUtils.lerp(
                this.cloneBuildBeam.material.opacity,
                0,
                0.16
            );
            return;
        }

        state.elapsed += delta;
        const t = Math.min(state.elapsed / state.duration, 1);
        const eased = THREE.MathUtils.smoothstep(t, 0, 1);
        const currentEnd = new THREE.Vector3().lerpVectors(
            state.from,
            state.to,
            eased
        );

        positions[0] = state.from.x;
        positions[1] = state.from.y;
        positions[2] = state.from.z;
        positions[3] = currentEnd.x;
        positions[4] = currentEnd.y;
        positions[5] = currentEnd.z;
        this.cloneBuildBeamGeometry.attributes.position.needsUpdate = true;

        this.cloneBuildBeam.material.opacity = 0.75;

        if (t >= 1) {
            state.active = false;

            if (this.pendingCloneEdgeKey) {
                this.clonedEdgeKeys.add(this.pendingCloneEdgeKey);
                this.pendingCloneEdgeKey = null;
            }
        }
    }

    updateGraphMotion(elapsed) {
        this.graphsRoot.rotation.y = Math.sin(elapsed * 0.22) * 0.08;
        this.graphsRoot.rotation.x = Math.cos(elapsed * 0.18) * 0.025;
        this.graphsRoot.position.y = Math.sin(elapsed * 0.6) * 0.06;
    }

    updateOriginalNodes(elapsed) {
        this.originalNodes.forEach((node, nodeId) => {
            const isActive = nodeId === this.activeOriginalNodeId;
            const isDone = this.processedNodeIds.has(nodeId);
            const isVisited = this.visitedNodeIds.has(nodeId);
            const isQueued = this.enqueuedNodeIds.has(nodeId);

            let targetColor = ORIGINAL_NODE_COLOR;
            let targetScale = 1;
            let targetEmissiveIntensity = 0.22;

            if (isVisited) {
                targetColor = ORIGINAL_NODE_VISITED_COLOR;
                targetEmissiveIntensity = 0.34;
            }

            if (isQueued) {
                targetScale = 1.08;
                targetEmissiveIntensity = 0.45;
            }

            if (isDone) {
                targetColor = ORIGINAL_NODE_DONE_COLOR;
                targetEmissiveIntensity = 0.38;
            }

            if (isActive) {
                targetColor = ORIGINAL_NODE_ACTIVE_COLOR;
                targetScale = 1.18 + Math.sin(elapsed * 4.0) * 0.03;
                targetEmissiveIntensity = 0.82;
            }

            const nextScale = THREE.MathUtils.lerp(node.scale.x, targetScale, 0.12);
            node.scale.setScalar(nextScale);
            node.material.color.lerp(targetColor, 0.12);
            node.material.emissive.lerp(targetColor, 0.12);
            node.material.emissiveIntensity = THREE.MathUtils.lerp(
                node.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.12
            );
        });
    }

    updateCloneNodes(elapsed) {
        this.cloneNodes.forEach((node, nodeId) => {
            const isVisible = this.clonedNodeIds.has(nodeId);
            const isActive = nodeId === this.activeCloneNodeId;
            const isDone = this.processedNodeIds.has(nodeId);

            let targetScale = isVisible ? 1 : 0.001;
            let targetOpacity = isVisible ? 1 : 0;
            let targetColor = CLONE_NODE_COLOR;
            let targetEmissiveIntensity = 0.26;

            if (isDone) {
                targetColor = CLONE_NODE_DONE_COLOR;
                targetEmissiveIntensity = 0.34;
            }

            if (isActive) {
                targetColor = CLONE_NODE_ACTIVE_COLOR;
                targetScale = 1.22 + Math.sin(elapsed * 4.0) * 0.04;
                targetOpacity = 1;
                targetEmissiveIntensity = 0.88;
            }

            const nextScale = THREE.MathUtils.lerp(node.scale.x, targetScale, 0.12);
            node.scale.setScalar(nextScale);

            setMaterialOpacity(
                node.material,
                THREE.MathUtils.lerp(node.material.opacity, targetOpacity, 0.12)
            );

            node.material.color.lerp(targetColor, 0.12);
            node.material.emissive.lerp(targetColor, 0.12);
            node.material.emissiveIntensity = THREE.MathUtils.lerp(
                node.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.12
            );
        });
    }

    updateOriginalEdges() {
        this.originalEdges.forEach((edge, nextEdgeKey) => {
            const isActive = nextEdgeKey === this.activeOriginalEdgeKey;
            const targetOpacity = isActive ? 1 : 0.42;
            const targetColor = isActive
                ? ORIGINAL_EDGE_ACTIVE_COLOR
                : ORIGINAL_EDGE_COLOR;

            edge.material.color.lerp(targetColor, 0.14);
            edge.material.opacity = THREE.MathUtils.lerp(
                edge.material.opacity,
                targetOpacity,
                0.14
            );
        });
    }

    updateCloneEdges() {
        this.cloneEdges.forEach((edge, nextEdgeKey) => {
            const isCommitted = this.clonedEdgeKeys.has(nextEdgeKey);
            const isPending = nextEdgeKey === this.pendingCloneEdgeKey;
            const isActive = nextEdgeKey === this.activeCloneEdgeKey;

            const targetOpacity = isCommitted ? (isActive ? 1 : 0.82) : 0;

            const targetColor = isActive
                ? CLONE_EDGE_ACTIVE_COLOR
                : CLONE_EDGE_COLOR;

            edge.material.color.lerp(targetColor, 0.14);
            edge.material.opacity = THREE.MathUtils.lerp(
                edge.material.opacity,
                isPending ? 0 : targetOpacity,
                0.14
            );
        });
    }

    updateActiveRings(elapsed) {
        this.updateRing(
            this.originalActiveRing,
            this.originalPositions,
            this.activeOriginalNodeId,
            elapsed
        );

        this.updateRing(
            this.cloneActiveRing,
            this.clonePositions,
            this.activeCloneNodeId,
            elapsed
        );
    }

    updateRing(ring, positionMap, activeNodeId, elapsed) {
        if (!activeNodeId || !positionMap.has(activeNodeId)) {
            ring.material.opacity = THREE.MathUtils.lerp(
                ring.material.opacity,
                0,
                0.14
            );
            return;
        }

        const position = positionMap.get(activeNodeId);
        ring.position.copy(position);
        ring.position.y -= 0.45;
        ring.scale.setScalar(1 + Math.sin(elapsed * 4.0) * 0.05);
        ring.material.opacity = THREE.MathUtils.lerp(
            ring.material.opacity,
            0.9,
            0.14
        );
    }

    onDispose() {
        this.originalNodes = null;
        this.cloneNodes = null;
        this.originalEdges = null;
        this.cloneEdges = null;
        this.originalPositions = null;
        this.clonePositions = null;
        this.graphsRoot = null;
        this.originalActiveRing = null;
        this.cloneActiveRing = null;
        this.transferPulse = null;
        this.cloneBuildBeam = null;
        this.transferPulseState = null;
        this.cloneEdgeBuildState = null;
    }
}